/**
 * Permission tree definition for the QB system.
 *
 * Maps 1:1 to the Figma design (node 242:22280 — "New group creating").
 * Each leaf is a permission flag. Parent nodes represent module groupings
 * and can be toggled to enable/disable all children.
 */

/**
 * Permission keys mirror the navigation 1:1 — the main system menu (AppAside)
 * plus the project submenu (the 15 PROJECT_SUBMODULES under `projects`). So a
 * package/role grants exactly the same options the user sees in the nav.
 */
export type PermissionKey =
  // Main menu
  | 'dashboard'
  | 'projects'
  // Project submenu (PROJECT_SUBMODULES)
  | 'projects.workLog'
  | 'projects.approvals'
  | 'projects.workReleases'
  | 'projects.progress'
  | 'projects.elements'
  | 'projects.customer'
  | 'projects.inspectionsSystem'
  | 'projects.plans'
  | 'projects.labTesting'
  | 'projects.structures'
  | 'projects.infoCenter'
  | 'projects.crafts'
  | 'projects.professions'
  | 'projects.stages'
  // Main menu (cont.)
  | 'reports'
  | 'infoCenter'
  | 'plans'
  | 'iso'
  | 'companies'
  | 'packages'
  | 'usersManagement'
  | 'usersManagement.users'
  | 'usersManagement.roles'
  | 'systemSettings'
  | 'systemSettings.crafts'
  | 'systemSettings.standards'
  | 'systemSettings.professions'
  | 'systemSettings.positions'
  | 'systemSettings.stages'
  | 'systemSettings.inspectionsSystem'
  | 'systemPreferences';

/**
 * Sub-option group attached to a leaf permission.
 * Figma node 2478:136677 — rows for projects.progress, projects.element,
 * projects.qc, projects.lab carry an extra row with two checkboxes
 * (Show / Interaction). Stored as flat booleans in
 * JobRolePermissionEntry.options keyed by `${optionGroup}.show` and
 * `${optionGroup}.interact`.
 */
export interface PermissionSubOptionGroup {
  /**
   * Stable group key — also used as the prefix for the boolean keys
   * persisted in JobRolePermissionEntry.options.
   *   group `progressStatus`  → options `progressStatus.show`,
   *                             `progressStatus.interact`
   *   group `managerConfirmation` → `managerConfirmation.show`,
   *                                 `managerConfirmation.interact`
   */
  groupKey: 'progressStatus' | 'managerConfirmation';
  /** i18n key for the group label shown to the left of the checkboxes. */
  i18nKey: string;
}

/**
 * A single-checkbox role permission flag (one tick, no Show/Interaction pair).
 * Roles-only — packages ignore it. Persisted in JobRolePermissionEntry.options
 * keyed by `key` (e.g. `workLogApprover`).
 */
export interface PermissionFlag {
  key: string;
  /** i18n key for the label shown next to the single checkbox. */
  i18nKey: string;
}

export interface PermissionNode {
  key: PermissionKey;
  /** i18n key — must exist in Translations.<i18nKey>. */
  i18nKey: string;
  children?: PermissionNode[];
  /** Optional Show/Interaction sub-row below the main row. */
  options?: PermissionSubOptionGroup;
  /** Optional single-checkbox role flags, each its own sub-row. */
  flags?: PermissionFlag[];
}

/**
 * Canonical permission tree — mirrors the navigation 1:1 (updated 2026-06-04).
 * Top level = the main system menu (AppAside `BASE_MENU`); `projects` children =
 * the project submenu (PROJECT_SUBMODULES); `usersManagement` / `systemSettings`
 * children = their Aside sub-items. Keep this in sync whenever the nav changes.
 */
export const PERMISSION_TREE: readonly PermissionNode[] = [
  { key: 'dashboard', i18nKey: 'dashboard' },
  {
    key: 'projects',
    i18nKey: 'projects',
    children: [
      {
        key: 'projects.workLog',
        i18nKey: 'workLog',
        options: { groupKey: 'progressStatus', i18nKey: 'settings' },
        // "Work log approver" moved to the per-user Project settings (gear).
      },
      { key: 'projects.approvals', i18nKey: 'approvals' },
      { key: 'projects.workReleases', i18nKey: 'workReleases' },
      { key: 'projects.progress', i18nKey: 'progress' },
      {
        key: 'projects.elements',
        i18nKey: 'elements',
        options: { groupKey: 'managerConfirmation', i18nKey: 'managerStatus' },
      },
      { key: 'projects.customer', i18nKey: 'customer2' },
      { key: 'projects.inspectionsSystem', i18nKey: 'inspectionsSystem' },
      { key: 'projects.plans', i18nKey: 'plans' },
      {
        key: 'projects.labTesting',
        i18nKey: 'labTesting',
        options: { groupKey: 'managerConfirmation', i18nKey: 'managerStatus' },
      },
      { key: 'projects.structures', i18nKey: 'structures' },
      { key: 'projects.infoCenter', i18nKey: 'infoCenter' },
      { key: 'projects.crafts', i18nKey: 'crafts' },
      { key: 'projects.professions', i18nKey: 'professions' },
      { key: 'projects.stages', i18nKey: 'stages' },
    ],
  },
  { key: 'reports', i18nKey: 'reports' },
  { key: 'infoCenter', i18nKey: 'infoCenter' },
  { key: 'plans', i18nKey: 'plans' },
  { key: 'iso', i18nKey: 'iso' },
  { key: 'companies', i18nKey: 'companies' },
  { key: 'packages', i18nKey: 'packages' },
  {
    key: 'usersManagement',
    i18nKey: 'usersManagement',
    children: [
      { key: 'usersManagement.users', i18nKey: 'users' },
      { key: 'usersManagement.roles', i18nKey: 'roles' },
    ],
  },
  {
    key: 'systemSettings',
    i18nKey: 'systemSettings',
    children: [
      { key: 'systemSettings.crafts', i18nKey: 'crafts' },
      { key: 'systemSettings.standards', i18nKey: 'standards' },
      { key: 'systemSettings.professions', i18nKey: 'professions' },
      { key: 'systemSettings.positions', i18nKey: 'positions' },
      { key: 'systemSettings.stages', i18nKey: 'stages' },
      { key: 'systemSettings.inspectionsSystem', i18nKey: 'inspectionsSystem' },
    ],
  },
  { key: 'systemPreferences', i18nKey: 'systemPreferences' },
] as const;

/** Flat set of all permission keys (for validation). */
export const ALL_PERMISSION_KEYS: ReadonlySet<PermissionKey> = (() => {
  const set = new Set<PermissionKey>();
  const walk = (nodes: readonly PermissionNode[]): void => {
    for (const n of nodes) {
      set.add(n.key);
      if (n.children) walk(n.children);
    }
  };
  walk(PERMISSION_TREE);
  return set;
})();
